export interface VoiceCaptureRoute {
  readonly captureId: string;
  readonly token: string | null;
}

interface VoiceCaptureLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

interface SessionTokenStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Consume the recording-link secret before telemetry starts. The URL fragment
 * is never sent to the server, and replaceUrl removes it before Sentry can
 * observe the initial page location.
 */
export function consumeVoiceCaptureRoute(args: {
  readonly location: VoiceCaptureLocation;
  readonly storage: SessionTokenStore;
  readonly replaceUrl: (url: string) => void;
}): VoiceCaptureRoute | null {
  const match = /^\/voice-recording\/([0-9a-f-]{36})\/?$/i.exec(args.location.pathname);
  const captureId = match?.[1];
  if (!captureId) return null;

  const storageKey = `cloneai.voice-capture.${captureId}`;
  const fragmentToken = args.location.hash.slice(1);
  if (/^[A-Za-z0-9_-]{43}$/.test(fragmentToken)) {
    args.replaceUrl(`${args.location.pathname}${args.location.search}`);
    try {
      args.storage.setItem(storageKey, fragmentToken);
    } catch {
      // Storage can be unavailable in strict privacy modes. The in-memory
      // token still permits the current page to finish onboarding.
    }
    return { captureId, token: fragmentToken };
  }

  try {
    return { captureId, token: args.storage.getItem(storageKey) };
  } catch {
    return { captureId, token: null };
  }
}
